import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import CopyToClipboard from "@cloudscape-design/components/copy-to-clipboard";
import KeyValuePairs from "@cloudscape-design/components/key-value-pairs";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import { checkoutOrderCode } from "../../router/routes";
import { navigate, useLocation } from "../../router/useRoute";
import { usePublicOrder } from "../../api/queries";
import { errorMessage } from "../../api/client";
import type { PublicOrder } from "../../api/types";
import { PublicShell } from "./PublicShell";

const VND = new Intl.NumberFormat("vi-VN");

/** Trạng thái đã chốt và câu nói tương ứng. */
const SETTLED: Record<string, { type: "success" | "error" | "warning"; title: string; body: string }> = {
  paid: {
    type: "success",
    title: "Đã nhận thanh toán",
    body: "Quyền đã được mở. Quay lại cửa sổ FaceGo để dùng ngay — không cần đăng nhập lại.",
  },
  cancelled: { type: "error", title: "Đơn đã huỷ", body: "Mở lại FaceGo và tạo đơn mới." },
  expired: { type: "error", title: "Đơn đã hết hạn", body: "Mở lại FaceGo và tạo đơn mới." },
  failed: { type: "error", title: "Đơn không hoàn tất", body: "Mở lại FaceGo và tạo đơn mới." },
};

export function CheckoutPage() {
  const { pathname } = useLocation();
  const orderCode = checkoutOrderCode(pathname);
  const query = usePublicOrder(orderCode);
  const order = query.data?.order;
  const settled = order ? SETTLED[order.status] : undefined;

  return (
    <PublicShell
      title={`Thanh toán đơn ${orderCode}`}
      description={
        settled ? undefined : "Quét mã bằng app ngân hàng, hoặc chuyển khoản tay theo thông tin bên dưới."
      }
      hero="compact"
    >
      {query.isLoading ? <StatusIndicator type="loading">Đang tải đơn hàng</StatusIndicator> : null}
      {query.isError ? (
        <Alert type="error" header="Không tìm thấy đơn hàng này">
          {errorMessage(query.error)}
        </Alert>
      ) : null}
      {order && settled ? (
        <SpaceBetween size="m">
          <StatusIndicator type={settled.type}>{settled.title}</StatusIndicator>
          <Box variant="p" color="text-body-secondary">{settled.body}</Box>
          <Button
            href="/member"
            onFollow={(event) => {
              event.preventDefault();
              navigate("/member");
            }}
          >
            Về trang thành viên
          </Button>
        </SpaceBetween>
      ) : null}
      {order && !settled ? <PendingOrder order={order} /> : null}
    </PublicShell>
  );
}

function PendingOrder({ order }: { order: PublicOrder }) {
  return (
    <SpaceBetween size="l">
      {order.qr_url ? (
        <Box textAlign="center">
          {/*
            Khổ cố định 280px. Ảnh VietQR về ở kích thước lớn hơn nhiều; để nó
            tràn theo container thì trên màn rộng mã QR to quá mức cần thiết mà
            vẫn không quét nhanh hơn, còn trên điện thoại lại tràn ra ngoài thẻ.
          */}
          <img
            src={order.qr_url}
            alt={`Mã VietQR chuyển ${VND.format(order.amount)} đồng cho đơn ${order.order_code}`}
            style={{ width: "min(280px, 100%)", height: "auto" }}
          />
        </Box>
      ) : (
        <Alert type="warning" header="Chưa tạo được mã QR">
          Chuyển khoản thủ công theo thông tin bên dưới. Số tiền và nội dung phải khớp tuyệt đối.
        </Alert>
      )}

      <KeyValuePairs
        columns={2}
        items={[
          { label: "Số tiền", value: <strong>{VND.format(order.amount)}đ</strong> },
          { label: "Ngân hàng", value: order.bank_code || "—" },
          { label: "Số tài khoản", value: <Copyable text={order.bank_account} name="Số tài khoản" /> },
          { label: "Chủ tài khoản", value: order.bank_account_name || "—" },
          {
            label: "Nội dung chuyển khoản",
            value: <Copyable text={order.transfer_content} name="Nội dung chuyển khoản" />,
          },
        ]}
      />

      {/*
        Sai nội dung là lỗi tốn người nhất trong cả luồng: webhook không ghép
        được giao dịch với đơn, và phải đối soát tay. Nói trước, ngay cạnh chỗ
        người dùng sắp gõ.
      */}
      <Alert type="warning">
        Giữ nguyên nội dung chuyển khoản. Sai nội dung thì hệ thống không ghép được giao dịch với
        đơn này và phải đối soát tay.
      </Alert>

      <SpaceBetween size="xs">
        <StatusIndicator type="loading">Đang chờ ngân hàng báo có</StatusIndicator>
        <Box variant="small" color="text-body-secondary">
          Trang tự cập nhật khi tiền vào; không cần tải lại.
          {order.expires_at
            ? ` Đơn có hạn đến ${new Date(order.expires_at).toLocaleString("vi-VN")}.`
            : ""}
        </Box>
      </SpaceBetween>
    </SpaceBetween>
  );
}

/**
 * Số tài khoản và nội dung chuyển khoản là hai thứ người dùng phải nhập lại khi
 * app ngân hàng không quét được mã. Gõ tay một chuỗi 12 ký tự là chỗ sinh lỗi,
 * nên cho copy một chạm — và giữ font mono để phân biệt 0 với O, 1 với l.
 */
function Copyable({ text, name }: { text?: string; name: string }) {
  if (!text) return <>—</>;
  return (
    <CopyToClipboard
      variant="inline"
      textToCopy={text}
      copyButtonAriaLabel={`Sao chép ${name.toLowerCase()}`}
      copySuccessText={`Đã chép ${name.toLowerCase()}`}
      copyErrorText={`Không chép được ${name.toLowerCase()}`}
    />
  );
}
